import { detectRTLOffsetType } from '../../../utils';

/**
 * Computes and returns the browser-specific scrollLeft to use for a normalized scroll position in RTL or LTR layouts.
 *
 * In most browsers, container.scrollLeft expects a value in the local coordinate system and interpretation varies for RTL containers:
 *   - "negative": scrollLeft is zero at right edge, negative at left (e.g., Firefox).
 *   - "reverse": scrollLeft max at left edge, zero at right (e.g., Edge, IE).
 *   - "default": scrollLeft behaves as in LTR (e.g., Chrome, Safari).
 *
 * This function takes a normalized target value (where 0 means fully scrolled to the right for RTL),
 * and returns the appropriate scrollLeft value to set on the container for the current browser RTL mode.
 *
 * Use this when you want to scroll to a specific normalized position in the container and require cross-browser RTL consistency.
 *
 * @param container - The scroll container element.
 * @param value - The normalized scrollLeft position (0 = rightmost in RTL; increases leftward).
 * @param isRTL - True if the container is rendered in RTL mode.
 * @returns The browser-specific scrollLeft value for assignment to container.scrollLeft.
 */
export function getScrollLeftForNormalizedValue(container: HTMLElement, value: number, isRTL: boolean): number {
    if (!isRTL) return value;

    const type = detectRTLOffsetType();

    switch (type) {
        case 'negative':
            return value - (container.scrollWidth - container.clientWidth);
        case 'reverse':
            return container.scrollWidth - container.clientWidth - value;
        default:
            return value;
    }
}
